import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster, TooltipProvider } from "@diametral/design-system/react";

import "@diametral/design-system/css/diametral.css";
import "./styles/app.css";
import "./styles/pilot.css";

import { useTranslation } from "./lib/i18n";
import { config } from "./lib/config";
import { keycloak, loginIdpHint } from "./lib/keycloak";
import { createQueryClient } from "./lib/queryClient";
import { applyTheme, storedTheme } from "./lib/theme";
import { BootError } from "./pages/BootError";
import { routes } from "./router";

// Before the first paint, so a dark-theme user never sees a white flash.
applyTheme(storedTheme());

const root = createRoot(document.getElementById("root")!);

// Gate the whole app behind Keycloak. `init` deliberately omits `onLoad`: its `login-required`
// branch redirects through an internal call that cannot carry `idpHint`, so we redirect
// ourselves below (same single round trip, but hintable).
keycloak
  .init({ pkceMethod: "S256", checkLoginIframe: false })
  .then((authenticated) => {
    if (!authenticated) {
      const idpHint = loginIdpHint(window.location.search, config.idpHint);
      void keycloak.login(idpHint ? { idpHint } : undefined);
      return;
    }
    // Keep the session alive while the tab is open (api() also refreshes before each call).
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => void keycloak.login());
    };
    // Created after init: keycloak-js strips the authorization code from the URL first.
    const router = createBrowserRouter(routes);
    const queryClient = createQueryClient();
    // A language switch remounts the screens (the router and the query cache are kept): module-level
    // labels and formatters are read again in the new language.
    function Localized() {
      const { i18n } = useTranslation();
      return <RouterProvider key={i18n.resolvedLanguage} router={router} />;
    }
    root.render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster>
              <Localized />
            </Toaster>
          </TooltipProvider>
        </QueryClientProvider>
      </StrictMode>,
    );
  })
  .catch(() => {
    root.render(<BootError keycloakUrl={config.keycloakUrl} />);
  });
